import {
	Circuit,
	Delegation,
	Disclaimers,
	Discord,
	Github,
	Grafana,
	GraphCircle,
	IFStaking,
	IconProps,
	LP,
	LendBorrow,
	Overview,
	Search,
	Settings,
	Share,
	Spot,
	Stats,
	Terms,
	Trade,
	Transfer,
	Twitter,
	Drift,
	Compare,
	Stake,
	Amplify,
	SSS,
	Bank,
} from '@drift-labs/icons';
import { MarketType } from '@drift-labs/sdk';
import {
	DRIFT_DISCORD_URL,
	DRIFT_GRAFANA_METRICS_URL,
} from 'src/constants/constants';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import {
	NavigationCategory,
	DesktopNavigationOptions,
	NavigationOption,
	NavigationOptionKeyWithoutError,
} from 'src/components/Navigation/types';
import {
	makeRouteResult,
	makeWindowResult,
	makeModalResult,
	filterByHideCondition,
} from 'src/components/Navigation/config/utils';

/**
 * This file stores the base config for navigation across desktop and mobile.
 *
 * Explanation for how things are split:
 *
 * Properties of Desktop navigation items:
 * - Category
 * - Icon
 * - Name
 * - Description
 * - Destination
 *
 * Properties of Mobile navigation items:
 * - Mobile
 * - Icon
 * - Name (THESE ARE DIFFERENT FROM DESKTOP FOR THE SAME DESTINATION)
 * - Destination
 *
 * Common properties for desktop+mobile:
 * - Destination
 * - Icon
 *
 * -------
 *
 * Types of handlers:
 *  - Navigate to a hardcoded route
 *  - Do some arbitrary thing like display a modal
 *
 * -------
 *
 * ADDING A NEW PAGE:
 * - Add a new key to the NavigationOptionKey enum
 * - Add a new key to the NavigationCategory enum (if applicable)
 * - Add a new entry to the BASE_NAVIGATION_OPTIONS object
 * - Add a new entry to the DESKTOP_NAVIGATION_OPTIONS object
 * - Add a new entry to the MOBILE_OPTIONS_ICONS object (if applicable)
 */

export const BASE_NAVIGATION_OPTIONS: {
	[key in NavigationOptionKeyWithoutError]: NavigationOption;
} = {
	stats: {
		key: 'stats',
		handler: () => makeRouteResult('/stats'),
		icon: Stats,
	},
	grafanaDashboard: {
		key: 'grafanaDashboard',
		handler: () => makeWindowResult(DRIFT_GRAFANA_METRICS_URL),
		icon: Grafana,
	},
	perpetuals: {
		key: 'perpetuals',
		handler: () =>
			// hardcode to SOL-PERP, because navigating to / will display current selected market in DriftStore,
			// and if the market happens to be a prediction market, we will render the prediction market on the
			// trade page, which we currently don't want
			{
				const result = makeRouteResult(
					getIsZeroFeesActiveForMarket({ marketIndex: 1, isPerp: true })
						? '/BTC-PERP'
						: '/SOL-PERP',
					{
						isMarketNavigation: true,
						marketNavigationType: MarketType.PERP,
					}
				);
				return result;
			},
		icon: Trade,
	},
	spot: {
		key: 'spot',
		handler: () =>
			makeRouteResult('/', {
				isMarketNavigation: true,
				marketNavigationType: MarketType.SPOT,
			}),
		icon: Spot,
	},
	swap: {
		key: 'swap',
		handler: () => makeRouteResult('/swap'),
		icon: Transfer,
	},
	insuranceStaking: {
		key: 'insuranceStaking',
		handler: () => makeRouteResult('/vaults/insurance-fund-vaults'),
		icon: IFStaking,
	},
	borrowLend: {
		key: 'borrowLend',
		handler: () => makeRouteResult('/earn/borrow-lend'),
		icon: LendBorrow,
	},
	driftEarn: {
		key: 'driftEarn',
		handler: () => makeRouteResult('/earn'),
		icon: Compare,
	},
	portfolio: {
		key: 'portfolio',
		handler: () => makeRouteResult('/portfolio/trade'),
		icon: Overview,
	},
	settings: {
		key: 'settings',
		handler: () => makeModalResult({ type: 'settings', props: {} }),
		icon: Settings,
	},
	docs: {
		key: 'docs',
		handler: () => makeWindowResult('https://docs.drift.trade'),
		icon: Search,
	},
	referrals: {
		key: 'referrals',
		handler: () => makeRouteResult('/referrals'),
		icon: Delegation,
	},
	github: {
		key: 'github',
		handler: () => makeWindowResult('https://github.com/drift-labs'),
		icon: Github,
	},
	twitter: {
		key: 'twitter',
		handler: () => makeWindowResult('https://twitter.com/DriftProtocol'),
		icon: Twitter,
	},
	discord: {
		key: 'discord',
		handler: () => makeWindowResult(DRIFT_DISCORD_URL),
		icon: Discord,
	},
	feedback: {
		key: 'feedback',
		handler: () => makeWindowResult(DRIFT_DISCORD_URL),
		icon: Share,
	},
	terms: {
		key: 'terms',
		handler: () =>
			makeWindowResult(
				'https://docs.drift.trade/legal-and-regulations/terms-of-use'
			),
		icon: Terms,
	},
	disclaimers: {
		key: 'disclaimers',
		handler: () =>
			makeWindowResult(
				'https://docs.drift.trade/legal-and-regulations/disclaimer'
			),
		icon: Disclaimers,
	},
	fundingHistory: {
		key: 'fundingHistory',
		handler: () => makeRouteResult('/funding?market=SOL-PERP'),
		icon: GraphCircle,
	},
	marketMakingVaults: {
		key: 'marketMakingVaults',
		handler: () => makeRouteResult('/earn/mm-vaults'),
		icon: Circuit,
	},
	amplify: {
		key: 'amplify',
		handler: () => makeRouteResult('/earn/amplify'),
		icon: Amplify,
	},
	superstakesol: {
		key: 'superstakesol',
		handler: () => makeWindowResult('https://superstakesol.com'),
		icon: SSS,
	},
	insuranceFundStakingHistory: {
		key: 'insuranceFundStakingHistory',
		handler: () => makeRouteResult('/portfolio/history?table=if-staking'),
		icon: IFStaking,
	},
	driftStaking: {
		key: 'driftStaking',
		handler: () => makeRouteResult('/drift-safety-module'),
		icon: Drift,
	},
	bet: {
		key: 'bet',
		handler: () => makeRouteResult('/bet'),
		icon: Trade,
	},
	dSolStaking: {
		key: 'dSolStaking',
		handler: () => makeRouteResult('/earn/dsol-liquid-staking'),
		icon: LP,
	},
	vaults: {
		key: 'vaults',
		handler: () => makeRouteResult('/vaults/strategy-vaults'),
		icon: Stake,
	},
	leaderboard: {
		key: 'leaderboard',
		handler: () => makeRouteResult('/leaderboard/usd1'),
		icon: Stats,
	},
	governance: {
		key: 'governance',
		handler: () => makeWindowResult('https://drift.foundation'),
		icon: Drift,
	},
	institutional: {
		key: 'institutional',
		handler: () => makeRouteResult('/institutional'),
		icon: Bank,
	},
};

export const DESKTOP_NAVIGATION_OPTIONS: DesktopNavigationOptions = {
	portfolio: {
		type: 'staticOption',
		categoryName: 'portfolio',
		...BASE_NAVIGATION_OPTIONS.portfolio,
	},
	trade: {
		type: 'categoryOption',
		categoryName: 'trade',
		handler: BASE_NAVIGATION_OPTIONS.perpetuals.handler,
		options: [
			{
				...BASE_NAVIGATION_OPTIONS.perpetuals,
				name: 'Perpetuals',
				description: `Trade the hottest perp markets with up to ${Env.perpsMaxHighLeverage}x leverage`,
			},
			{
				...BASE_NAVIGATION_OPTIONS.spot,
				name: 'Spot',
				description: 'Trade assets with up to 5x leverage',
			},
			{
				...BASE_NAVIGATION_OPTIONS.swap,
				name: 'Swap',
				description: 'Swap any pair with up to 5x leverage',
			},
		].filter(filterByHideCondition),
	},
	earn: {
		type: 'categoryOption',
		categoryName: 'earn',
		handler: BASE_NAVIGATION_OPTIONS.borrowLend.handler,
		options: [
			{
				...BASE_NAVIGATION_OPTIONS.driftEarn,
				name: 'Explore & Compare',
				description: 'See yield across each product for all tokens',
			},
			{
				...BASE_NAVIGATION_OPTIONS.borrowLend,
				name: 'Borrow/Lend',
				description: 'Earn yield on your deposits through lending',
			},
			{
				...BASE_NAVIGATION_OPTIONS.amplify,
				name: 'Amplify',
				description: 'Earn leveraged yield with 1-click',
			},
			{
				...BASE_NAVIGATION_OPTIONS.dSolStaking,
				name: 'dSOL Liquid Staking',
				description: `Liquid stake your SOL with Drift to earn yield`,
			},
			{
				...BASE_NAVIGATION_OPTIONS.superstakesol,
				name: 'super stake sol',
				description: 'Earn leveraged yield on SOL staking tokens with 1-click',
				isExternalLink: true,
			},
		].filter(filterByHideCondition),
	},
	vaults: {
		type: 'categoryOption',
		categoryName: 'vaults',
		options: [
			{
				...BASE_NAVIGATION_OPTIONS.vaults,
				name: 'Strategy Vaults',
				description:
					'Maximize your yields with trading strategies from leading teams across Solana',
			},
			{
				...BASE_NAVIGATION_OPTIONS.insuranceStaking,
				name: 'Insurance Fund Vaults',
				description:
					'Stake your assets into a vault and earn yield from exchange fees',
			},
		].filter(filterByHideCondition),
	},
	leaderboard: {
		type: 'staticOption',
		categoryName: 'leaderboard',
		...BASE_NAVIGATION_OPTIONS.leaderboard,
	},
	more: {
		type: 'categoryOption',
		categoryName: 'more',
		options: [
			{
				...BASE_NAVIGATION_OPTIONS.stats,
				name: 'Stats',
				description: 'Funding, bankruptcies, liquidations and protocol stats',
			},
			{
				...BASE_NAVIGATION_OPTIONS.driftStaking,
				name: 'Stake DRIFT',
				description:
					'Stake your DRIFT tokens to participate in governance and more',
			},
			{
				...BASE_NAVIGATION_OPTIONS.institutional,
				name: 'Drift Institutional',
				description: 'The gateway to institutional DeFi on Solana',
				hideCondition: true,
			},
			{
				...BASE_NAVIGATION_OPTIONS.referrals,
				name: 'Referrals',
				description:
					'Refer friends to Drift and earn a share of their trading fees',
			},
			{
				...BASE_NAVIGATION_OPTIONS.bet,
				name: 'BET',
				description: 'Trade prediction markets on Drift',
			},
			{
				...BASE_NAVIGATION_OPTIONS.grafanaDashboard,
				name: 'Grafana Dashboard',
				description:
					'Deep dive into the health of each market and the protocol',
				isExternalLink: true,
			},
			{
				...BASE_NAVIGATION_OPTIONS.governance,
				name: 'Governance',
				description:
					'DRIFT is a governance token powering the Drift ecosystem.',
				isExternalLink: true,
			},
			{
				...BASE_NAVIGATION_OPTIONS.feedback,
				name: 'Help & Feedback',
				description: 'Get help, submit product feedback and ideas',
				isExternalLink: true,
			},
		].filter(filterByHideCondition),
	},
};

export const MOBILE_NAVIGATION_OPTIONS = DESKTOP_NAVIGATION_OPTIONS;

// The new mobile navigation has the same options but different icons mapped to the categories and static options
export const MOBILE_OPTIONS_ICONS: Partial<
	Record<NavigationCategory, (allProps: IconProps) => React.JSX.Element>
> = {
	portfolio: Overview,
	trade: Trade,
	earn: IFStaking,
	more: Stats,
	vaults: Stake,
	leaderboard: Stats,
};
